import { useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Skeleton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useAlert } from '../../core/AlertContext';
import { ReactionKind } from '../../Interfaces/IEventInteraction';
import {
    useCreateComment,
    useDeleteComment,
    useEventComments,
    useToggleReaction,
    useUpdateComment,
} from '../../hooks/eventInteractions';
import EventCommentItem from './EventCommentItem';
import { primaryButtonSx } from '../../core/pvtaStyles';

interface EventCommentSectionProps {
    eventId: number;
}

const parseBackendErrorMessage = (error: unknown): string => {
    if (!(error instanceof Error)) {
        return 'Kommentar konnte nicht gespeichert werden.';
    }

    const marker = ' - ';
    const markerIndex = error.message.indexOf(marker);
    if (markerIndex === -1) {
        return error.message;
    }

    const payload = error.message.slice(markerIndex + marker.length);
    try {
        const parsed = JSON.parse(payload) as Record<string, unknown>;
        const fieldMessages = Object.entries(parsed)
            .map(([field, value]) => {
                if (Array.isArray(value) && value.length > 0) {
                    return `${field}: ${String(value[0])}`;
                }
                if (typeof value === 'string') {
                    return `${field}: ${value}`;
                }
                return null;
            })
            .filter((value): value is string => Boolean(value));

        if (fieldMessages.length > 0) {
            return fieldMessages.join(' | ');
        }
    } catch (_parseError) {
        return payload;
    }

    return error.message;
};

const EventCommentSection: React.FC<EventCommentSectionProps> = ({ eventId }) => {
    const { showAlert } = useAlert();

    const [newComment, setNewComment] = useState<string>('');

    const {
        comments,
        isLoadingEventComments,
        isErrorEventComments,
        errorEventComments,
    } = useEventComments(eventId);
    const createCommentMutation = useCreateComment();
    const updateCommentMutation = useUpdateComment();
    const deleteCommentMutation = useDeleteComment();
    const toggleReactionMutation = useToggleReaction();

    const currentUserId = useMemo(() => {
        const value = localStorage.getItem('user_id');
        if (!value) {
            return null;
        }

        const parsed = Number(value);
        return Number.isNaN(parsed) ? null : parsed;
    }, []);

    const isWorking =
        createCommentMutation.isPending ||
        updateCommentMutation.isPending ||
        deleteCommentMutation.isPending ||
        toggleReactionMutation.isPending;

    const handleCreateRootComment = async (): Promise<void> => {
        const content = newComment.trim();
        if (!content) {
            return;
        }

        try {
            await createCommentMutation.mutateAsync({
                event_id: eventId,
                text: content,
                parent_id: null,
            });
            setNewComment('');
            showAlert({ severity: 'success', text: 'Kommentar erstellt.' });
        } catch (error) {
            showAlert({ severity: 'error', text: parseBackendErrorMessage(error) });
        }
    };

    const handleCreateReply = async (parentId: number, content: string): Promise<void> => {
        const trimmedContent = content.trim();
        if (!trimmedContent) {
            return;
        }

        try {
            await createCommentMutation.mutateAsync({
                event_id: eventId,
                text: trimmedContent,
                parent_id: parentId,
            });
            showAlert({ severity: 'success', text: 'Antwort erstellt.' });
        } catch (error) {
            showAlert({ severity: 'error', text: parseBackendErrorMessage(error) });
            throw error;
        }
    };

    const handleUpdateComment = async (commentId: number, content: string): Promise<void> => {
        const trimmedContent = content.trim();
        if (!trimmedContent) {
            return;
        }

        try {
            await updateCommentMutation.mutateAsync({
                eventId,
                comment_id: commentId,
                text: trimmedContent,
            });
            showAlert({ severity: 'success', text: 'Kommentar aktualisiert.' });
        } catch (error) {
            showAlert({ severity: 'error', text: parseBackendErrorMessage(error) });
            throw error;
        }
    };

    const handleDeleteComment = async (commentId: number): Promise<void> => {
        try {
            await deleteCommentMutation.mutateAsync({
                event_id: eventId,
                comment_id: commentId,
            });
            showAlert({ severity: 'success', text: 'Kommentar gelöscht.' });
        } catch (error) {
            showAlert({ severity: 'error', text: parseBackendErrorMessage(error) });
            throw error;
        }
    };

    const handleToggleReaction = async (commentId: number, kind: ReactionKind): Promise<void> => {
        try {
            await toggleReactionMutation.mutateAsync({
                event_id: eventId,
                comment_id: commentId,
                emoji: kind,
            });
        } catch (error) {
            showAlert({ severity: 'error', text: parseBackendErrorMessage(error) });
            throw error;
        }
    };

    return (
        <Card variant="outlined" sx={{ borderRadius: 2.5, height: '100%' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Kommentare
                </Typography>

                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        placeholder="Kommentar schreiben"
                        value={newComment}
                        onChange={({ target }) => setNewComment(target.value)}
                        disabled={isWorking}
                    />
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={1}
                        sx={{ mt: 1 }}
                    >
                        <Button
                            variant="text"
                            size="small"
                            onClick={() => setNewComment('')}
                            disabled={isWorking || !newComment.trim()}
                            sx={{ minWidth: 0, px: 1.5 }}
                        >
                            Abbrechen
                        </Button>
                        <Button
                            size="small"
                            onClick={() => void handleCreateRootComment()}
                            disabled={isWorking || !newComment.trim()}
                            sx={{ ...primaryButtonSx, minWidth: 0, px: 1.5 }}
                        >
                            Senden
                        </Button>
                    </Stack>
                </Box>

                {isErrorEventComments && (
                    <Alert severity="error" sx={{ mb: 1.5 }}>
                        {parseBackendErrorMessage(errorEventComments)}
                    </Alert>
                )}

                {isLoadingEventComments && (
                    <Stack spacing={1.5} sx={{ py: 1 }}>
                        {[0, 1].map((item) => (
                            <Card key={item} variant="outlined" sx={{ borderRadius: 2 }}>
                                <CardContent>
                                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                                        <Skeleton variant="circular" width={40} height={40} />
                                        <Box sx={{ flex: 1 }}>
                                            <Skeleton variant="text" width="30%" height={24} />
                                            <Skeleton variant="text" width="22%" height={18} />
                                            <Skeleton variant="text" width="100%" />
                                            <Skeleton variant="text" width="82%" />
                                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                                <Skeleton variant="rounded" width={64} height={28} />
                                                <Skeleton variant="rounded" width={64} height={28} />
                                            </Stack>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                )}

                {!isLoadingEventComments && comments.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        Noch keine Kommentare.
                    </Typography>
                )}

                {!isLoadingEventComments && comments.map((comment) => (
                    <EventCommentItem
                        key={comment.id}
                        comment={comment}
                        currentUserId={currentUserId}
                        isWorking={isWorking}
                        onCreateReply={handleCreateReply}
                        onUpdateComment={handleUpdateComment}
                        onDeleteComment={handleDeleteComment}
                        onToggleReaction={handleToggleReaction}
                    />
                ))}
            </CardContent>
        </Card>
    );
};

export default EventCommentSection;
